/**
 * GOLD STANDARD Notification System
 * Simple, reliable, production-ready implementation
 * 
 * Features:
 * - 10-second real-time polling
 * - Accurate badge counts
 * - Clean UI/UX
 * - Optimistic updates
 * - Error handling
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { notificationsAPI } from '../lib/api';

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollIntervalRef = useRef(null);
  const mountedRef = useRef(true);

  // Fetch unread count only
  const fetchUnreadCount = useCallback(async () => {
    if (!mountedRef.current) return;
    
    try {
      const response = await notificationsAPI.getUnreadCount();
      if (mountedRef.current && response.data) {
        setUnreadCount(response.data.count || 0);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
      // Silently fail for count updates (non-critical)
    }
  }, []);

  // Fetch full notification list
  const fetchNotifications = useCallback(async () => {
    if (!mountedRef.current) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await notificationsAPI.getAll({ 
        status: 'all',
        limit: 50
      });
      
      if (mountedRef.current) {
        // Handle response data
        const data = response?.data || {};
        const notifs = Array.isArray(data.notifications) ? data.notifications : [];
        
        console.log('[NotificationBell] Loaded', notifs.length, 'notifications');
        
        setNotifications(notifs);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('[NotificationBell] Error fetching:', err);
      console.error('[NotificationBell] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      if (mountedRef.current) {
        setError('Failed to load notifications. Please try again.');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Start polling for unread count
  const startPolling = useCallback(() => {
    // Initial fetch
    fetchUnreadCount();
    
    // Poll every 10 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchUnreadCount();
    }, 10000);
  }, [fetchUnreadCount]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Mount/unmount lifecycle
  useEffect(() => {
    mountedRef.current = true;
    startPolling();

    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    // Optimistic update
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, status: 'read' } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await notificationsAPI.markAsRead([notificationId]);
      // Refresh count to ensure accuracy
      await fetchUnreadCount();
    } catch (err) {
      console.error('Error marking as read:', err);
      // Revert on error
      await fetchNotifications();
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter(n => n.status === 'unread')
      .map(n => n.id);

    if (unreadIds.length === 0) return;

    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
    setUnreadCount(0);

    try {
      await notificationsAPI.markAllAsRead();
      await fetchUnreadCount();
    } catch (err) {
      console.error('Error marking all as read:', err);
      // Revert on error
      await fetchNotifications();
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (notification.status === 'unread') {
      await markAsRead(notification.id);
    }

    // Navigate to deep link if available
    if (notification.metadata?.deep_link) {
      setOpen(false);
      navigate(notification.metadata.deep_link);
    }
  };

  // Format relative time
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffSecs < 60) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return '';
    }
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(!open)}
        className="relative"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white animate-in zoom-in-50">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown Panel */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          {/* Panel */}
          <div className="absolute right-0 top-12 z-50 w-96 max-w-[calc(100vw-2rem)] rounded-lg border bg-white shadow-xl animate-in slide-in-from-top-2 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="text-base font-semibold">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllAsRead}
                    className="h-7 text-xs"
                  >
                    Mark all read
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setOpen(false)}
                  className="h-7 w-7"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : error ? (
                <div className="p-8 text-center">
                  <p className="text-sm text-red-600">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchNotifications}
                    className="mt-3"
                  >
                    Retry
                  </Button>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-12 text-center">
                  <Bell className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                  <p className="text-sm font-medium text-gray-900">No notifications</p>
                  <p className="mt-1 text-xs text-gray-500">
                    You're all caught up!
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`cursor-pointer px-4 py-3 transition-colors hover:bg-gray-50 ${
                        notification.status === 'unread' ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 leading-snug">
                            {notification.title}
                          </p>
                          {notification.message && (
                            <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                              {notification.message}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            {formatTimeAgo(notification.created_at)}
                          </p>
                        </div>
                        {notification.status === 'unread' && (
                          <div className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {!loading && !error && notifications.length > 0 && (
              <div className="border-t bg-gray-50 px-4 py-2.5 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setOpen(false);
                    navigate('/notifications');
                  }}
                  className="h-7 text-xs text-primary hover:text-primary/80"
                >
                  View all notifications →
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
